import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { swalConfirm } from "../../../../utils/swalAlert";
import {
  getCollections,
  createCollection,
  deleteCollection,
} from "../../../../api/product_management/collectionApi";
import "../../../../styles/panel/user/manageShop/EditShopShowcase.css";
import {
  BACK_ICON,
  CLOSE_ICON,
  HELP_ICON,
  PLUS_ICON,
  SHOWCASE_ICON,
  TRASH_ICON,
  Icon,
} from "./shared/EditShopShared";

const GUIDE_EXAMPLES = [
  { emoji: "🔥", title: "پیشنهاد ویژه", text: "محصولات برگزیده هفته" },
  {
    emoji: "🎁",
    title: "تخفیف نوروز",
    text: "محصولاتی که در فروش نوروزی هستند",
  },
  { emoji: "🆕", title: "جدیدترین‌ها", text: "آخرین محصولات اضافه‌شده" },
];

/**
 * مدیریت ویترین‌های یک فروشگاه.
 * شناسه‌ی فروشگاه (shopId) از پارامتر مسیر خونده میشه.
 */
export default function EditShopShowcase() {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const onBack = () => navigate(-1);

  const [guideOpen, setGuideOpen] = useState(false);
  const [showcases, setShowcases] = useState([]);
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchShowcases = async () => {
      try {
        const showCase = await getCollections(shopId);
        setShowcases(showCase.data || []);
      } catch (err) {
        console.error(err);
      }
    };

    if (shopId) {
      fetchShowcases();
    }
  }, [shopId]);

  const handleAdd = async () => {
    const trimmed = name.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const show = await createCollection(shopId, {
        name: trimmed,
      });
      setShowcases((prev) => [...prev, show.data]);
      setName("");
      toast.success("ویترین با موفقیت اضافه شد");
    } catch (err) {
      console.log(err);
      setError("افزودن ویترین با خطا مواجه شد، دوباره امتحان کن.");
      toast.error("افزودن ویترین با خطا مواجه شد");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (showcaseId, showcaseName) => {
    const confirmed = await swalConfirm({
      title: "حذف ویترین",
      text: showcaseName
        ? `آیا از حذف ویترین «${showcaseName}» مطمئن هستی؟`
        : "آیا از حذف این ویترین مطمئن هستی؟",
      confirmText: "بله، حذف کن",
      cancelText: "انصراف",
      danger: true,
    });

    if (!confirmed) return;

    try {
      await deleteCollection(shopId, showcaseId);
      setShowcases((prev) =>
        prev.filter((s) => (s.id ?? s.showcase_id) !== showcaseId),
      );
      toast.success("ویترین حذف شد");
    } catch (err) {
      console.log(err);
      toast.error("حذف ویترین با خطا مواجه شد");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="shop-showcase">
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            fontFamily: "inherit",
            direction: "rtl",
            fontSize: "13.5px",
          },
        }}
      />
      <div className="esw-head">
        <button className="esp-back-btn" onClick={onBack} aria-label="بازگشت">
          <Icon>{BACK_ICON}</Icon>
        </button>
        <div className="esw-head-text">
          <h1>ویترین</h1>
          <p>
            محصولات پرطرفدار یا مناسبتی رو گروه‌بندی کن تا در صفحه اصلی فروشگاهت
            نمایش داده بشن.
          </p>
        </div>
        <button className="esw-help-btn" onClick={() => setGuideOpen(true)}>
          <Icon>{HELP_ICON}</Icon>
          <span>راهنمای ویترین</span>
        </button>
      </div>

      <div className="esw-add-card">
        <label className="esw-add-label" htmlFor="esw-name-input">
          اسم ویترین جدید
        </label>
        <div className="esw-add-row">
          <input
            id="esw-name-input"
            className="esw-input"
            type="text"
            placeholder="مثلاً پیشنهاد ویژه"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button
            className="esw-add-btn"
            onClick={handleAdd}
            disabled={!name.trim() || submitting}
          >
            <Icon>{PLUS_ICON}</Icon>
            {submitting ? "در حال افزودن..." : "افزودن"}
          </button>
        </div>
        {error && <p className="esw-error">{error}</p>}
      </div>

      <div className="esw-list">
        {showcases.length === 0 ? (
          <div className="esw-empty">
            <span className="esw-empty-ic">
              <Icon>{SHOWCASE_ICON}</Icon>
            </span>
            هنوز ویترینی نساختی. از فرم بالا شروع کن.
          </div>
        ) : (
          showcases.map((s) => {
            const id = s.id ?? s.showcase_id;
            return (
              <div className="esw-item" key={id}>
                <span className="esw-item-ic">
                  <Icon>{SHOWCASE_ICON}</Icon>
                </span>
                <span className="esw-item-name">{s.name}</span>
                <button
                  className="esw-item-del"
                  onClick={() => handleDelete(id, s.name)}
                  aria-label="حذف ویترین"
                >
                  <Icon>{TRASH_ICON}</Icon>
                </button>
              </div>
            );
          })
        )}
      </div>

      {guideOpen && (
        <div
          className="esw-overlay"
          onClick={(e) => e.target === e.currentTarget && setGuideOpen(false)}
        >
          <div className="esw-modal">
            <div className="esw-modal-head">
              <div>
                <h3>📌 راهنمای ویترین</h3>
                <p>
                  ویترین یک گروه‌بندی موقت یا مناسبتی از محصولات است — مثل
                  «پیشنهاد ویژه»، «تخفیف نوروز» یا «جدیدترین‌ها».
                </p>
              </div>
              <button
                className="esw-modal-close"
                onClick={() => setGuideOpen(false)}
                aria-label="بستن"
              >
                <Icon>{CLOSE_ICON}</Icon>
              </button>
            </div>

            <div className="esw-examples">
              <span className="esw-examples-title">🛍 مثال ویترین‌ها</span>
              <ul>
                {GUIDE_EXAMPLES.map((ex) => (
                  <li key={ex.title}>
                    <span className="esw-example-emoji">{ex.emoji}</span>
                    <div>
                      <b>{ex.title}</b>
                      <span>{ex.text}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="esw-modal-section">
              <h4>چطور کار می‌کند؟</h4>
              <p>
                هر محصول می‌تواند به یک ویترین اضافه شود. سپس در مدیریت صفحه
                اصلی، می‌توانید یک ویترین انتخاب کنید تا محصولات آن در یک بخش
                خاص نمایش داده شوند.
              </p>
            </div>

            <div className="esw-modal-section esw-modal-diff">
              <h4>تفاوت با دسته‌بندی</h4>
              <p>
                دسته‌بندی ساختار دائمی فروشگاه است، اما ویترین برای نمایش‌های
                موقت و مناسبتی استفاده می‌شود.
              </p>
            </div>

            <div className="esw-modal-foot">
              <button
                className="esw-primary-btn"
                onClick={() => setGuideOpen(false)}
              >
                متوجه شدم
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
